<template>
  <v-dialog v-model="dialog" max-width="600">
    <v-card class="common-wage-transaction-modal">
      <v-card-title class="modal-header">
        <div class="header-content">
          <v-icon color="error" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="modal-title">거래명세서 생성</span>
        </div>
        <v-btn icon variant="text" @click="closeDialog" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="modal-body">
        <div class="info-row">
          <span class="info-label">대상 시설:</span>
          <span class="info-value">{{ memberName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">서비스 연월:</span>
          <span class="info-value">{{ year }}년 {{ month }}월</span>
        </div>

        <div class="form-section">
          <div class="form-row">
            <label>작성일자</label>
            <YmdDateInput v-model="form.writeDate" />
          </div>

          <div class="form-row">
            <label>수량 (인원)</label>
            <v-text-field
              v-model.number="form.wageCount"
              type="number"
              variant="outlined"
              density="compact"
              hide-details
              @input="calculateTotal"
            />
          </div>

          <div class="form-row">
            <label>단가</label>
            <v-text-field
              v-model.number="form.wageUnitPrice"
              type="number"
              variant="outlined"
              density="compact"
              hide-details
              @input="calculateTotal"
            />
          </div>

          <div class="form-row">
            <label>총액</label>
            <v-text-field
              :model-value="formatNumber(form.wageTotalPrice)"
              variant="outlined"
              density="compact"
              hide-details
              readonly
              class="total-field"
            />
          </div>

          <div class="form-row">
            <label>비고</label>
            <v-text-field
              v-model="form.wageBigo"
              variant="outlined"
              density="compact"
              hide-details
              placeholder="비고 입력"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-btn variant="text" class="btn-cancel" @click="closeDialog">취소</v-btn>
        <v-spacer />
        <v-btn 
          variant="flat" 
          class="btn-save" 
          @click="generatePdf" 
          :loading="loading"
          :disabled="!form.wageCount || form.wageCount <= 0"
        >
          PDF 생성
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- PDF 미리보기 다이얼로그 -->
  <v-dialog v-model="pdfDialog" max-width="900">
    <v-card class="pdf-preview-modal">
      <v-card-title class="modal-header">
        <span class="modal-title">거래명세서 미리보기</span>
        <v-btn icon variant="text" @click="pdfDialog = false" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="modal-body">
        <div v-if="pdfSrc" class="pdf-preview">
          <iframe
            :src="pdfSrc"
            width="100%"
            height="600"
            frameborder="0"
          />
        </div>
      </v-card-text>
      <v-card-actions class="modal-footer">
        <v-btn variant="text" class="btn-close" @click="pdfDialog = false">닫기</v-btn>
        <v-spacer />
        <v-btn variant="flat" class="btn-save" @click="downloadPdf">다운로드</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import YmdDateInput from '@/components/common/YmdDateInput.vue';
import axios from 'axios';
import { tokenManager } from '@/services/api';

interface Props {
  mCode?: string | number;
  memberName?: string;
  bizGubun?: string;
  year?: number;
  month?: number;
  dataCount?: number;
}

const props = defineProps<Props>();

const dialog = ref(false);
const pdfDialog = ref(false);
const loading = ref(false);
const pdfData = ref<string | null>(null);

const form = reactive({
  writeDate: new Date().toISOString().split('T')[0],
  wageCount: 0,
  wageUnitPrice: 17000,
  wageTotalPrice: 0,
  wageBigo: ''
});

const pdfSrc = computed(() => {
  if (pdfData.value) {
    return `data:application/pdf;base64,${pdfData.value}`;
  }
  return '';
});

const formatNumber = (value: number) => {
  return value.toLocaleString();
};

const calculateTotal = () => {
  form.wageTotalPrice = (form.wageCount || 0) * (form.wageUnitPrice || 0);
};

const getAuthHeaders = () => {
  const token = tokenManager.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const generatePdf = async () => {
  loading.value = true;
  try {
    const response = await axios.post('/api/common-wage/transaction-pdf', {
      mCode: props.mCode,
      memberName: props.memberName,
      year: props.year,
      month: props.month,
      bizGubun: props.bizGubun,
      writeDate: form.writeDate,
      wageCount: form.wageCount,
      wageUnitPrice: form.wageUnitPrice,
      wageBigo: form.wageBigo
    }, {
      headers: getAuthHeaders()
    });

    if (response.data.success) {
      pdfData.value = response.data.pdf;
      pdfDialog.value = true;
      dialog.value = false;
    } else {
      alert('PDF 생성에 실패했습니다.');
    }
  } catch (error) {
    console.error('거래명세서 PDF 생성 오류:', error);
    alert('PDF 생성 중 오류가 발생했습니다.');
  } finally {
    loading.value = false;
  }
};

const downloadPdf = () => {
  if (pdfData.value) {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${pdfData.value}`;
    link.download = `거래명세서_${props.memberName}_${props.year}${String(props.month).padStart(2, '0')}.pdf`;
    link.click();
  }
};

const openDialog = (count?: number) => {
  form.writeDate = new Date().toISOString().split('T')[0];
  form.wageCount = count || props.dataCount || 0;
  form.wageUnitPrice = 17000;
  form.wageBigo = '';
  calculateTotal();
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
};

defineExpose({ openDialog });
</script>
